import * as Cesium from 'cesium';
import { EntityMapEntity } from './facility-pin.service';
import { center } from '@turf/turf';
import { CameraService } from './camera.service';
import { CoreService } from './core.service';
import { WindowService } from './window.service';
import { DataContextService } from './data-context.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TimelineService } from './timeline.service';
export class ShapeMapEntity extends EntityMapEntity {
  constructor(mapEntityName: string, mapEntityLayerPath: string,cameraService: CameraService,
    coreService: CoreService,windowService: WindowService,dataContext: DataContextService,modalService: NgbModal,timelineService: TimelineService) {
    super(mapEntityName, mapEntityLayerPath,cameraService,coreService,windowService,dataContext,modalService,timelineService);
  }

  override buildEntity(): void {
    super.buildEntity();
  }

  override getCesiumEntity(): any {
    return super.buildEntity();
  }
}
export class SimplePolygonMapEntity extends ShapeMapEntity {
  locations: any;
  extrudedHeight: number;
  height: number;
  material: any;
  outlineColor: any;
  showOutline = false;
  outlineWidth = 1.0;
  declare timestamp: Date;
  declare cesiumEntity: any;

  constructor(
    mapEntityName: string,
    mapEntityLayerPath: string,
    locations: any,
    altitude: number,
    height: number,
    coreService: CoreService,
    windowService: WindowService,
    dataContext: DataContextService,
    modalService: NgbModal, timelineService: TimelineService,
    material?: any
  ) {
    super(mapEntityName, mapEntityLayerPath, locations,coreService,windowService,dataContext,modalService,timelineService);
    this.locations = locations;
    this.extrudedHeight = altitude;
    this.height = height;
    this.material = material ?? new Cesium.Color(1.0, 0.0, 0.0, 0.2);
    this.outlineColor = undefined;
    this.showOutline = false;
    this.outlineWidth = 1.0;

    if (locations !== undefined) {
        let geoJsonPolygon = locations;
  // If locations is a raw array, wrap it as a GeoJSON Polygon
  if (Array.isArray(locations) && Array.isArray(locations[0]) && typeof locations[0][0] === 'number') {
    geoJsonPolygon = {
      type: 'Polygon',
      coordinates: [locations]
    };
  }
      const centerLocations = center(geoJsonPolygon);
      if (
        centerLocations &&
        centerLocations.geometry &&
        Array.isArray(centerLocations.geometry.coordinates) &&
        centerLocations.geometry.coordinates.length === 2
      ) {
        this.setCenterLatitude(centerLocations.geometry.coordinates[0]);
        this.setCenterLongitude(centerLocations.geometry.coordinates[1]);
      }
    }
  }

  setMaterial(material: any): void {
    this.material = material;
    if (this.cesiumEntity?.polygon) {
      this.cesiumEntity.polygon.material = this.material;
    }
  }

  getMaterial(): any {
    return this.material;
  }

  setOutline(outlineColor: any): void {
    this.outlineColor = outlineColor;
    if (this.cesiumEntity?.polygon) {
      this.cesiumEntity.polygon.outlineColor = this.outlineColor;
    }
  }

  setOutlineWidth(outlineWidth: number): void {
    this.outlineWidth = outlineWidth;
    if (this.cesiumEntity?.polygon) {
      this.cesiumEntity.polygon.outlineWidth = this.outlineWidth;
    }
  }

  setShowOutline(showOutline: boolean): void {
    this.showOutline = showOutline;
    if (this.cesiumEntity?.polygon) {
      this.cesiumEntity.polygon.outline = this.showOutline;
    }
  }

  setCoordinates(locations: any): void {
    this.locations = locations;
    const centerLocations = center(locations);
    if (
      centerLocations &&
      centerLocations.geometry &&
      Array.isArray(centerLocations.geometry.coordinates) &&
      centerLocations.geometry.coordinates.length === 2
    ) {
      this.setCenterLatitude(centerLocations.geometry.coordinates[1]);
      this.setCenterLongitude(centerLocations.geometry.coordinates[0]);
    }
    if (this.cesiumEntity?.polygon) {
      this.setHeirarchy();
    }
  }

  setHeight(height: number): void {
    this.height = height;
    if (this.cesiumEntity?.polygon) {
      this.setHeirarchy();
    }
  }

  getHeight(): number {
    return this.height;
  }

  setAltitude(extrudedHeight: number): void {
  this.extrudedHeight = extrudedHeight;
  if (this.cesiumEntity?.polygon) {
    this.cesiumEntity.polygon.perPositionHeight = undefined;
    // Fix: Always set an explicit height to disable terrain clamping
    this.cesiumEntity.polygon.height = extrudedHeight;
    this.cesiumEntity.polygon.extrudedHeight = this.extrudedHeight;
  }
}

  getAltitude(): number {
    return this.extrudedHeight;
  }

  getCoordinates(): any {
    return this.locations;
  }

  arrayOfLocationsToArrayOfPoints(locations: any): number[] {
    const arrayOfPoints: number[] = [];
    for (let i = 0; i < locations.length; i++) {
      arrayOfPoints.push(locations[i][1]);
      arrayOfPoints.push(locations[i][0]);
      if (this.height) {
        arrayOfPoints.push(this.height);
      }
    }
    return arrayOfPoints;
  }

  setHeirarchy(): void {
    if (!this.cesiumEntity) return;
    
    if (this.height) {
      this.cesiumEntity.polygon = {
        hierarchy: Cesium.Cartesian3.fromDegreesArrayHeights(
          this.arrayOfLocationsToArrayOfPoints(this.locations)
        ),
        // Fix: Explicitly set height to disable terrain clamping
        height: this.height,
      };
    } else {
      this.cesiumEntity.polygon = {
        hierarchy: Cesium.Cartesian3.fromDegreesArray(
          this.arrayOfLocationsToArrayOfPoints(this.locations)
        ),
        // Fix: Set height to 0 to disable terrain clamping for outlines
        height: 0,
      };
    }
    this.cesiumEntity.polygon.material = this.material;
  }

  override buildEntity(): void {
    super.buildEntity();
    this.setHeirarchy();

    if (this.extrudedHeight !== undefined) {
      this.cesiumEntity.polygon.extrudedHeight = this.extrudedHeight;
      this.cesiumEntity.polygon.perPositionHeight = undefined;
    }

    if (this.outlineColor !== undefined && this.showOutline === true) {
      this.cesiumEntity.polygon.outlineColor = this.outlineColor;
      this.cesiumEntity.polygon.outline = true;
      this.cesiumEntity.polygon.outlineWidth = this.outlineWidth;
      this.cesiumEntity.polygon.perPositionHeight = undefined;
    }

    if (this.timestamp !== undefined) {
      const startDate = Cesium.JulianDate.fromDate(this.timestamp);
      const endDate = new Cesium.JulianDate(3448257, 0); // 1000 years in future
      this.cesiumEntity.availability = new Cesium.TimeIntervalCollection([
        new Cesium.TimeInterval({ start: startDate, stop: endDate }),
      ]);
    }
  }

  override getCesiumEntity(): any {
    if (this.cesiumEntity === undefined) {
      this.buildEntity();
    }
    return this.cesiumEntity;
  }
}
export class AreaMapEntity extends SimplePolygonMapEntity {
  override id: string;
  perimeterId: string;
  transparency = 0.05; // Reduced from 0.1 for less brightness
  label: string;
  perimeters: Record<string, any> = {};
  dataTimeSlices: any;
  override isVisibleInOverlappingEntitiesUI = false;
  isFloorPerimeter = true;
  building: any;
  isPartOfBuilding?: boolean;
  floorPlan?: any;
  isFloor?: boolean;
  floorNumber?: number;
  rotationCoords?: any;
  deaconData?: any;
  floorplanId: any;

  constructor(
    id: string,
    label: string,
    coordinates: any,
    elevation: number,
    alt: number,
    coreService: CoreService,
    windowService: WindowService,
    dataContext: DataContextService,
    modalService: NgbModal  ,timelineService: TimelineService
  ) {
    super(
      label,
      'ArcAngel//Areas',
      coordinates,
      alt,
      elevation,coreService,windowService, 
      dataContext,
      modalService,
      timelineService,
      Cesium.Color.LIGHTGRAY.withAlpha(0.02), // Reduced alpha for less brightness
      
    );
    this.id = 'area-' + id;
    this.perimeterId = id;
    this.label = label;
    //this.dataTimeSlices = createObjectSampleProperty();

    this.setShowInLayersUI(false);
    this.setOutline(Cesium.Color.BLACK.withAlpha(this.transparency * 2));
    this.setShowOutline(true);
    this.setOutlineWidth(1.0);

    this.removeDeleteCommand();
    // this.removeShowHideCommand();
    this.removeTimelineCommand();
    this.removeTrackCommand();
    // this.addAttribute(new FloorAttribute(this.id));
  }

  setLabel(label: string): void {
    this.label = label;
    this.name = label;
  }

  setParentBuilding(building: any): void {
    this.building = building;
    this.isPartOfBuilding = true;
  }

  getParentBuilding(): any {
    return this.building;
  }

  setFloorPlan(floorPlan: any): void {
    this.floorPlan = floorPlan;
    this.isFloor = true;
    this.isPartOfBuilding = true;
  }

  getFloorPlan(): any {
    return this.floorPlan;
  }

  setFloorNumber(number: number): void {
    this.floorNumber = number;
  }

  getFloorNumber(): number | undefined {
    return this.floorNumber;
  }

  setFloorPlanRotationCoords(rotationCoords: any): void {
    this.rotationCoords = rotationCoords;
  }

  getFloorPlanRotationCoords(): any {
    return this.rotationCoords;
  }

  setPerimeters(perimeters: any[]): void {
    perimeters.forEach(perimeter => {
      perimeter.setIsPartOfBuilding(true);
      this.perimeters[perimeter.arcAngelId] = perimeter;
    });
  }

  restoreTransparency(): void {
    Object.values(this.perimeters).forEach(perimeter => {
      perimeter.restoreTransparency();
    });
    this.transparency = 0.1;
    const material = Cesium.Color.fromAlpha(Cesium.Color.LIGHTGRAY, this.transparency);
    this.setMaterial(material);
    this.setOutline(Cesium.Color.BLACK.withAlpha(this.transparency * 2));
  }

  increaseTransparency(): void {
    Object.values(this.perimeters).forEach(perimeter => {
      perimeter.increaseTransparency();
    });
    this.transparency -= 0.02;
    const material = Cesium.Color.fromAlpha(Cesium.Color.LIGHTGRAY, this.transparency);
    this.setMaterial(material);
    this.setOutline(Cesium.Color.BLACK.withAlpha(this.transparency * 2));
  }

  getPerimeters(): any {
    return this.perimeters;
  }

  containsPerimeterId(id: string): boolean {
    return !!this.perimeters && id in this.perimeters;
  }

  getSensorIds(): any[] {
    return Object.values(this.perimeters).flatMap((p: any) => p.sensorIds || []);
  }

  getBeacons(): { [key: string]: any } {
    const map: { [key: string]: any } = {};
    Object.values(this.perimeters).forEach((perimeter: any) => {
      (perimeter.beacons || {}).forEach((beacon: any) => {
        map[beacon.id] = beacon;
        map[beacon.id].associatedPerimeter = perimeter;
      });
    });
    return map;
  }

  getNumberOfUnSafeUsers(): number {
    return Object.values(this.perimeters).reduce(
      (sum: number, perimeter: any) => sum + (perimeter.getListOfUnSafeUsers?.().length || 0),
      0
    );
  }

  getNumberOfUnknownUsers(): number {
    return Object.values(this.perimeters).reduce(
      (sum: number, perimeter: any) => sum + ((perimeter.listOfUnknownUsers || []).length),
      0
    );
  }

  getNumberOfSafeUsers(): number {
    return Object.values(this.perimeters).reduce(
      (sum: number, perimeter: any) => sum + ((perimeter.listOfSafeUsers || []).length),
      0
    );
  }

  getNumberOfFirstResponders(): number {
    return Object.values(this.perimeters).reduce(
      (sum: number, perimeter: any) => sum + ((perimeter.listOfFR || []).length),
      0
    );
  }

  getNumberOfDevices(): number {
    return Object.values(this.perimeters).reduce(
      (sum: number, perimeter: any) => sum + ((perimeter.devices || []).length),
      0
    );
  }

  getListOfUnknownUsers(): any[] {
    return Object.values(this.perimeters).flatMap((p: any) => p.listOfUnknownUsers || []);
  }

  getListOfSafeUsers(): any[] {
    return Object.values(this.perimeters).flatMap((p: any) => p.listOfSafeUsers || []);
  }

  getListOfUnSafeUsers(): any[] {
    return Object.values(this.perimeters).flatMap((o: any) =>
      o.getListOfUnSafeUsers ? o.getListOfUnSafeUsers() : []
    );
  }

  getListOfFirstResponders(): any[] {
    return Object.values(this.perimeters).flatMap((p: any) => p.listOfFR || []);
  }

  getListDevices(): any[] {
    return Object.values(this.perimeters).flatMap((p: any) => p.devices || []);
  }

  getTrackingDevices(): any {
    let trackers: any = {};
    Object.values(this.perimeters).forEach((perimeter: any) => {
      trackers = { ...trackers, ...(perimeter.trackingDevices || {}) };
    });
    return trackers;
  }

  getPerimeterFromBeaconId(beaconId: string): any {
    for (const perimeter of Object.values(this.perimeters)) {
      const beacons = perimeter.getBeacons?.() || {};
      if (beacons[beaconId]) {
        this.deaconData = beaconId;
        return perimeter;
      }
    }
    return undefined;
  }

  // override hide to hide other entities that are part of this area (perimeters)
  override hide(): void {
    super.hide();
    Object.values(this.perimeters).forEach((perim: any) => perim.hide());
  }

  override show(): void {
    super.show();
    Object.values(this.perimeters).forEach((perim: any) => perim.show());
  }

  getTimeSlice(julianDate: any): any {
    return this.dataTimeSlices.getValue(julianDate);
  }

  override async goToEntity(goToEntityCallback?: () => void): Promise<void> {
    // Camera.flyToEntity(this.getCesiumEntity(), new Cesium.HeadingPitchRange(0.0, -Math.PI / 5.0, 0.0));
    if (goToEntityCallback) {
      goToEntityCallback();
    }
  }

  getBeaconList(): any[] {
    const beacons: any[] = [];
    Object.values(this.perimeters).forEach((perimeter: any) => {
      (perimeter.beacons || {}).forEach((beacon: any) => {
        beacons.push(beacon);
      });
    });
    return beacons;
  }

  getIncidentUpdates(): any[] {
    const updates: any[] = [];
    const perimeterIds = Object.keys(this.perimeters);
    if (this.building && this.building.incidents && this.building.incidents.length > 0) {
      this.building.incidents.forEach((incident: any) => {
        (incident.updates || []).forEach((update: any) => {
          if (perimeterIds.includes(update.data.perimeterId)) {
            updates.push(update);
          }
        });
      });
    }
    return updates;
  }
}

